import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";

export interface ITag {
  updated_at: string;
  key: string;
  restaurant_id: ID;
  id: ID;
  created_at: string;
  model_name: string;
  value: string;
  model_pk: ID;
}

export interface ITagsListResponse extends APIPaginatedResponse<ITag> {}

export interface ITagsListAllResponse {
  pages: ITagsListResponse[];
  pageParams: number[];
}

export interface ITagsListParams {
  search?: string;
  tags?: string[];
  page?: number;
  page_size?: number;
  restaurant_id?: ID;
  useVenueSpecific?: boolean;
}

export interface IBaseTagCRUDParams {
  key: string;
  value: string;
}

export interface ITagCreateParams extends IBaseTagCRUDParams {
  model_pk: ID;
}

export interface ITagUpdateParams extends IBaseTagCRUDParams {
  id: ID;
}

export interface ITagDeleteParams {
  id: ID;
}

export interface ITagListUniqueParams {
  useVenueSpecific?: boolean;
}

export interface ITagListUniqueResponse {
  key: string;
  values: Array<string>;
}

export interface ITagsService {
  list: (params: ITagsListParams) => UseQueryResult<ITagsListResponse, Error>;
  listAll: (
    params: ITagsListParams,
  ) => UseInfiniteQueryResult<ITagsListAllResponse, Error>;
  listUnique: (
    params: ITagListUniqueParams,
  ) => UseQueryResult<Array<ITagListUniqueResponse>, Error>;
  create: () => UseMutationResult<ITag, Error, ITagCreateParams>;
  update: () => UseMutationResult<ITag, Error, ITagUpdateParams>;
  delete: () => UseMutationResult<boolean, Error, ITagDeleteParams>;
}

export class TagsService implements ITagsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params: ITagsListParams): UseQueryResult<ITagsListResponse, Error> {
    const { useVenueSpecific = false, ...restParams } = params;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "tags", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<ITagsListResponse>>(
          "/tagging/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: useVenueSpecific,
            params: restParams,
          },
        );

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  listAll(
    params: ITagsListParams,
  ): UseInfiniteQueryResult<ITagsListAllResponse, Error> {
    const { useVenueSpecific = false, ...restParams } = params;
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "tags", "list", "all"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<AxiosResponse<ITagsListResponse>>(
          "/tagging/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: useVenueSpecific,
            params: { ...restParams, page: pageParam, page_size: 100 },
          },
        );

        return data;
      },
      getNextPageParam: (lastPage: ITagsListResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  listUnique(
    params: ITagListUniqueParams,
  ): UseQueryResult<Array<ITagListUniqueResponse>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "tags", "list", "unique"],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<Array<ITagListUniqueResponse>>
        >("/tagging/options/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params,
        });
        return data;
      },
    });
  }

  create(): UseMutationResult<ITag, Error, ITagCreateParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "tags", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<ITag>>("/tagging/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params,
        });
        return data;
      },
      onMutate: async (newTag) => {},
      onError: (err, newTag, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["operations", "tags", "list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["operations", "tags", "list", "all"],
        });
        queryClient.invalidateQueries({
          queryKey: ["organisation", "relationships"],
        });
      },
    });
  }

  update(): UseMutationResult<ITag, Error, ITagUpdateParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "tags", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.patch<AxiosResponse<ITag>>(
          `/tagging/${params.id}/`,
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newTag) => {},
      onError: (err, newTag, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["operations", "tags", "list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["operations", "tags", "list", "all"],
        });
        queryClient.invalidateQueries({
          queryKey: ["organisation", "relationships"],
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, ITagDeleteParams> {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "tags", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/tagging/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: {},
          },
        );
        return true;
      },
      onMutate: async (params) => {},
      onError: (err, params, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["operations", "tags", "list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["operations", "tags", "list", "all"],
        });
        queryClient.invalidateQueries({
          queryKey: ["organisation", "relationships"],
        });
      },
    });
  }
}
